import { useEffect } from 'react';

/**
 * Revele les elements portant `data-reveal` quand ils entrent dans le viewport.
 *
 * Le masquage initial est porte par `.js-reveal [data-reveal]` en CSS : la classe
 * n'est posee qu'au montage, donc sans JS (ou si le hook ne tourne pas) le contenu
 * reste visible. On ne cache jamais du contenu qu'on ne saurait pas re-afficher.
 */
export function useScrollReveal() {
  useEffect(() => {
    const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (targets.length === 0) return;

    const root = document.documentElement;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      targets.forEach(el => el.classList.add('is-revealed'));
      return;
    }

    root.classList.add('js-reveal');

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target); // une seule fois : pas de clignotement au scroll inverse
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 },
    );

    targets.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
      root.classList.remove('js-reveal');
    };
  }, []);
}

export default useScrollReveal;
